import { DialogProps } from '@radix-ui/react-dialog'
import { Command as CommandPrimitive } from 'cmdk'
import pluralize from 'pluralize'
import * as React from 'react'

import { cn, commandScore } from '../utils'
import { Check, Checkbox } from './checkbox'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './dialog'
import { Icon } from './icon'
import { Spinner } from './spinner'

type CommandDialogProps = DialogProps

const CommandDialog = ({ children, ...props }: CommandDialogProps) => {
  return (
    <Dialog {...props}>
      <DialogContent className="overflow-hidden p-0 shadow-lg">
        <DialogHeader>
          <DialogTitle className="hidden" />
        </DialogHeader>
        <Command className="[&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group]]:px-2 [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 [&_[cmdk-input-wrapper]_svg]:h-5 [&_[cmdk-input-wrapper]_svg]:w-5 [&_[cmdk-input]]:h-12 [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-3 [&_[cmdk-item]_svg]:h-5 [&_[cmdk-item]_svg]:w-5">
          {children}
        </Command>
      </DialogContent>
    </Dialog>
  )
}

const Command = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive>
>(({ className, ...props }, ref) => (
  <CommandPrimitive
    filter={commandScore}
    ref={ref}
    data-command-root="true"
    className={cn(
      'text-popover-foreground flex h-full w-full flex-col overflow-hidden rounded-md',
      className,
    )}
    {...props}
  />
))
Command.displayName = CommandPrimitive.displayName

const CommandInput = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Input>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Input>
>(({ className, ...props }, ref) => {
  const wrapperRef = React.useRef<HTMLDivElement>(null)
  const onValueChange = React.useCallback(
    (value: string) => {
      requestAnimationFrame(() => {
        const commandList = wrapperRef?.current?.parentElement?.querySelector(
          '[data-command-list="true"]',
        )
        commandList?.scrollTo({ top: 0 })
      })
      props.onValueChange?.(value)
    },
    [props.onValueChange],
  )

  return (
    <div
      className="flex items-center border-b px-3"
      cmdk-input-wrapper=""
      ref={wrapperRef}
    >
      <Icon name="magnifying-glass" className="mr-2 h-4 w-4 shrink-0 opacity-50" />
      <CommandPrimitive.Input
        ref={ref}
        onValueChange={onValueChange}
        className={cn(
          'placeholder:text-muted-foreground flex h-11 w-full rounded-md bg-transparent py-3 outline-hidden disabled:cursor-not-allowed disabled:opacity-50',
          className,
        )}
        {...props}
      />
    </div>
  )
})

CommandInput.displayName = CommandPrimitive.Input.displayName

const CommandList = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.List>
>(({ className, ...props }, ref) => (
  <CommandPrimitive.List
    ref={ref}
    data-command-list="true"
    className={cn('max-h-[500px] overflow-x-hidden overflow-y-auto', className)}
    {...props}
  />
))

CommandList.displayName = CommandPrimitive.List.displayName

const CommandEmpty = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Empty>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Empty>
>((props, ref) => (
  <CommandPrimitive.Empty ref={ref} className="py-6 text-center" {...props} />
))

CommandEmpty.displayName = CommandPrimitive.Empty.displayName

const CommandExpandContext = (props: {
  availableResults: number
  optionalLabel?: string
  onSelect: () => void
}) => {
  return props.availableResults > 0 ? (
    <>
      <CommandSeparator />
      <CommandItem onSelect={props.onSelect}>
        Show {props.availableResults}{' '}
        {pluralize(props.optionalLabel || 'option', props.availableResults)} not showing
      </CommandItem>
    </>
  ) : null
}

const CommandLoading = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Empty>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Empty>
>((props, ref) => (
  <CommandPrimitive.Loading ref={ref} className="py-6 text-center text-sm" {...props} />
))

CommandLoading.displayName = CommandPrimitive.Loading.displayName

const CommandGroup = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Group>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Group>
>(({ className, ...props }, ref) => (
  <CommandPrimitive.Group
    ref={ref}
    className={cn(
      'text-foreground [&_[cmdk-group-heading]]:text-muted-foreground overflow-hidden p-1 [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium',
      className,
    )}
    {...props}
  />
))

CommandGroup.displayName = CommandPrimitive.Group.displayName

const CommandSeparator = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <CommandPrimitive.Separator
    ref={ref}
    className={cn('bg-border -mx-1 my-1 h-px', className)}
    {...props}
  />
))
CommandSeparator.displayName = CommandPrimitive.Separator.displayName

const CommandItem = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Item>
>(({ className, ...props }, ref) => (
  <CommandPrimitive.Item
    ref={ref}
    className={cn(
      'aria-selected:bg-popover-selected group relative flex cursor-pointer items-center rounded-md px-2 py-2 outline-hidden select-none data-[disabled="true"]:pointer-events-none data-[disabled="true"]:opacity-50',
      className,
    )}
    {...props}
  />
))

CommandItem.displayName = CommandPrimitive.Item.displayName

const CommandCheckItem = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Item> & {
    selected: boolean
    multiselect?: boolean
  }
>((props, ref) => {
  const children = props.children
  return (
    <CommandItem
      value={props.value}
      keywords={props.keywords}
      onSelect={props.onSelect}
      ref={ref}
      className="group flex items-center gap-3"
      aria-selected={!!props.selected}
    >
      {props.multiselect ? (
        <Checkbox checked={props.selected} />
      ) : (
        <div
          className={cn(
            'group-[[aria-selected=true]]:border-foreground relative aspect-square h-5 w-5 shrink-0 rounded-full border',
            {
              'bg-foreground border-foreground': props.selected,
            },
          )}
        >
          <Check
            className={cn(
              'relative top-[6px] left-1/2 mt-px hidden size-3 -translate-1/2',
              {
                block: props.selected,
              },
            )}
          />
        </div>
      )}
      <div
        className="flex max-w-[250px] shrink grow items-center gap-2 truncate"
        title={props.title || (typeof children === 'string' ? children : undefined)}
      >
        {children}
      </div>
    </CommandItem>
  )
})
CommandCheckItem.displayName = 'CommandCheckItem'

const CommandShortcut = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLSpanElement>) => {
  return (
    <span
      className={cn('text-muted-foreground ml-auto text-xs tracking-widest', className)}
      {...props}
    />
  )
}
CommandShortcut.displayName = 'CommandShortcut'

const CommandSpinner = React.forwardRef<HTMLDivElement>((_, ref) => {
  return (
    <div ref={ref} className="flex aspect-square items-center justify-center p-4">
      <Spinner size="40" />
    </div>
  )
})
CommandSpinner.displayName = 'CommandSpinner'

export {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandCheckItem,
  CommandShortcut,
  CommandSeparator,
  CommandLoading,
  CommandDialog,
  CommandExpandContext,
  CommandSpinner,
}
